import { Children, Fragment, cloneElement, isValidElement, useEffect, useMemo, useRef, type ReactElement, type ReactNode } from 'react'
import { Parser } from 'html-to-react'
import { InlineTreatmentFlag } from '@/citator/components/InlineTreatmentFlag'
import { CaseSummaryTooltip } from '@/citator/components/CaseSummaryTooltip'
import { CaseStatusEnum, isWarningCaseStatus } from '@/citator/schema/case-status-schema'
import type { OpinionCitationLink } from '@/citator/schema/opinion-citation-link-schema'
import { CitatorOpenedIn, CitatorPanelSource, CitatorSurface, mapCaseStatusToBadgeType } from '@/analytics/enums/citator.enums'
import { RiaCitatorSection } from '@/analytics/enums/ria.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackCitatorBadgeImpression, trackCitatorPanelOpened } from '@/analytics/utils/citator-analytics'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { buildCaselawStandaloneViewerURL, type CaselawViewerContext } from '@/standalone-reference-viewer/caselaw/utils/build-caselaw-viewer-url'
import { buildLrrStandaloneViewerURL } from '@/standalone-reference-viewer/lrr/utils/build-lrr-viewer-url'
import { openStandaloneViewerTab } from '@/standalone-reference-viewer/utils/open-standalone-viewer-tab'
import { annotateCaseCitationLinks, type AnnotatedCaseCitation } from '@/content-viewer/util/annotate-case-citation-links'
import { applyHighlights, sanitizeProviderHtmlWithCitationHints } from '@/content-viewer/util/sanitize-and-highlight-html'

type CaselawOpinionHtmlProps = {
  htmlContent: string
  citationLinks: OpinionCitationLink[]
  highlightSentences?: string[]
  viewerContext?: CaselawViewerContext
}

/** Render sanitized opinion HTML with optional runtime citation-link enrichment. */
export function CaselawOpinionHtml({ htmlContent, citationLinks, highlightSentences, viewerContext }: CaselawOpinionHtmlProps) {
  const { trackEvent } = useAnalytics()
  const firedFlagImpressionsRef = useRef(new Set<string>())
  const viewerConversationId = viewerContext?.conversationId
  const viewerMessageId = viewerContext?.messageId
  const viewerMatterId = viewerContext?.matterId
  const viewerTab = viewerContext?.tab
  const viewerSection = viewerContext?.section
  const hasViewerContext = viewerContext !== undefined

  const { parsedHtml, citations } = useMemo(() => {
    const navigationContext = hasViewerContext
      ? {
          conversationId: viewerConversationId,
          messageId: viewerMessageId,
          matterId: viewerMatterId,
          tab: viewerTab,
          section: viewerSection,
        }
      : undefined
    const targets = citationLinks.map((descriptor) => {
      const metadata = { s3_path: descriptor.s3_path }
      if (descriptor.document_type === 'lrr') {
        return {
          descriptor,
          opinionUrl: buildLrrStandaloneViewerURL(metadata, { matterId: viewerMatterId }) ?? '',
        }
      }

      const opinionUrl = buildCaselawStandaloneViewerURL(metadata, navigationContext)
      const citationAnalysisUrl = buildCaselawStandaloneViewerURL(metadata, {
        ...navigationContext,
        tab: CaseLawReferenceTab.citator,
        section: descriptor.case_status === CaseStatusEnum.overruling_risk ? RiaCitatorSection.CaseCitations : undefined,
      })
      return { descriptor, opinionUrl, citationAnalysisUrl }
    })
    const { sanitizedHtml, citationHints } = sanitizeProviderHtmlWithCitationHints(htmlContent)
    const annotated = annotateCaseCitationLinks(sanitizedHtml, targets, citationHints)
    const highlighted = applyHighlights(annotated.html, highlightSentences)
    return { parsedHtml: Parser().parse(highlighted), citations: annotated.citations }
  }, [citationLinks, hasViewerContext, highlightSentences, htmlContent, viewerConversationId, viewerMatterId, viewerMessageId, viewerSection, viewerTab])

  useEffect(() => {
    for (const citation of citations.values()) {
      const caseStatus = citation.descriptor.case_status
      if (citation.descriptor.document_type !== 'caselaw' || !isWarningCaseStatus(caseStatus)) continue

      const impressionKey = `${citation.descriptor.parent_id}:${caseStatus}`
      if (firedFlagImpressionsRef.current.has(impressionKey)) continue
      firedFlagImpressionsRef.current.add(impressionKey)
      trackCitatorBadgeImpression({
        trackEvent,
        surface: CitatorSurface.OpinionViewerInline,
        badgeType: mapCaseStatusToBadgeType(caseStatus),
      })
    }
  }, [citations, trackEvent])

  const openCitationAnalysis = (citation: AnnotatedCaseCitation) => {
    if (!citation.citationAnalysisUrl) return
    const opened = openStandaloneViewerTab(citation.citationAnalysisUrl)
    if (!opened) return
    trackCitatorPanelOpened({
      trackEvent,
      surface: CitatorSurface.OpinionViewerInline,
      source: CitatorPanelSource.BadgeClick,
      openedIn: CitatorOpenedIn.StandaloneViewer,
    })
  }

  return <>{decorateCitationAnchors(parsedHtml, citations, openCitationAnalysis)}</>
}

function decorateCitationAnchors(
  node: ReactNode,
  citations: Map<string, AnnotatedCaseCitation>,
  openCitationAnalysis: (citation: AnnotatedCaseCitation) => void
): ReactNode {
  if (Array.isArray(node)) return node.map((child) => decorateCitationAnchors(child, citations, openCitationAnalysis))
  if (!isValidElement(node)) return node

  const element = node as ReactElement<{ children?: ReactNode; 'data-case-citation-occurrence'?: string }>
  const children = Children.map(element.props.children, (child) => decorateCitationAnchors(child, citations, openCitationAnalysis))
  const occurrenceId = element.props['data-case-citation-occurrence']
  const citation = occurrenceId ? citations.get(occurrenceId) : undefined
  const decoratedElement = cloneElement(element, undefined, children)

  if (!citation) return decoratedElement

  const caseStatus = citation.descriptor.case_status
  return (
    <Fragment key={occurrenceId}>
      {citation.descriptor.document_type === 'caselaw' ? (
        <OpinionCitationSummaryLink citation={citation} onViewCitationAnalysis={() => openCitationAnalysis(citation)}>
          {decoratedElement}
        </OpinionCitationSummaryLink>
      ) : (
        decoratedElement
      )}
      {citation.descriptor.document_type === 'caselaw' && isWarningCaseStatus(caseStatus) && citation.citationAnalysisUrl && (
        <InlineTreatmentFlag
          caseStatus={caseStatus}
          surface={CitatorSurface.OpinionViewerInline}
          onViewCitationAnalysis={() => openCitationAnalysis(citation)}
          className="ml-0.5 align-text-bottom"
        />
      )}
    </Fragment>
  )
}

function OpinionCitationSummaryLink({
  citation,
  onViewCitationAnalysis,
  children,
}: {
  citation: AnnotatedCaseCitation
  onViewCitationAnalysis: () => void
  children: ReactElement
}) {
  return (
    <CaseSummaryTooltip
      title={citation.descriptor.title ?? citation.descriptor.citation_text}
      shortSummary={citation.descriptor.short_summary}
      caseStatus={citation.descriptor.case_status}
      onViewReference={() => {
        if (citation.opinionUrl) openStandaloneViewerTab(citation.opinionUrl)
      }}
      onViewCitationAnalysis={onViewCitationAnalysis}
    >
      {children}
    </CaseSummaryTooltip>
  )
}
